import React from "react";
import {
  AlertIcon,
  CalendarIcon,
  CircleCheckIcon,
  ClipboardCheckIcon,
  DownloadIcon,
  FileTextIcon,
  ShieldIcon,
  UsersRoundIcon,
} from "./landing/Icons";
import GuideDownload from "./landing/guide-download/GuideDownload";
import { LeadFormProvider } from "./landing/lead-form/LeadFormProvider";
import {
  Breadcrumb,
  CONTAINER,
  GRID_BACKDROP,
  IconTile,
  PrimaryButton,
  READING,
  SectionHeading,
  SectionLabel,
} from "./landing/ui";

// Dates and wording follow the MIDA, ESD and MOHA sources listed under the FAQs; re-check them when those change.
const KEY_POINTS = [
  "The Revised Employment Pass Salary Policy took effect on 1 June 2026.",
  "MIDA says the requirement to submit and comply with a succession plan starts on 1 January 2027.",
  "The policy is there to make sure expatriates pass their knowledge and expertise on to local employees.",
  "The ESD policy summary links succession plans to Employment Pass Categories II and III. Confirm the latest application requirements for your company.",
] as const;

const KEY_DATES = [
  { date: "14 January 2026", dateTime: "2026-01-14", event: "MOHA announced the revised policy." },
  { date: "1 June 2026", dateTime: "2026-06-01", event: "New salary thresholds and employment-duration limits took effect." },
  { date: "1 January 2027", dateTime: "2027-01-01", event: "MIDA says succession plan submission and compliance begin." },
] as const;

const PLAN_ELEMENTS = [
  { title: "Roles to transfer", body: "The expatriate positions and functions that will move to local employees." },
  { title: "Development activity", body: "Training, mentoring and knowledge transfer from expatriates to local employees." },
  { title: "Capability timeline", body: "A reasonable timeframe for local employees to build the skills and competencies the role needs." },
  { title: "Business continuity", body: "A transition that protects productivity and how the organisation performs." },
] as const;

const STEPS = [
  {
    title: "Confirm the roles and obligations",
    body: "Review the Employment Pass categories involved, renewal dates and the business owner responsible for each role. Confirm the latest requirement with the right authority or adviser.",
  },
  {
    title: "Map critical knowledge",
    body: "Write down the decisions, relationships, processes and technical expertise each expatriate role holds that need passing on.",
  },
  {
    title: "Identify local successors and gaps",
    body: "Assess possible local successors against the role and note the capabilities each person still needs to build.",
  },
  {
    title: "Design the development plan",
    body: "Combine training, structured mentoring, guided practice at work and knowledge-transfer activities around those gaps.",
  },
  {
    title: "Set milestones and ownership",
    body: "Agree a realistic timeline, named owners, review points and continuity measures for each role and successor.",
  },
  {
    title: "Keep evidence and review progress",
    body: "Record activity, feedback, learning applied at work and capability progress, so leaders can review the plan and update it when things change.",
  },
] as const;

// The four groups on the checklist page of the PDF.
const CHECKLIST_AREAS = ["Know your exposure", "Plan the transfer", "Run and evidence it", "Stay compliant"] as const;

const SUPPORT = [
  {
    Icon: UsersRoundIcon,
    title: "Structured mentoring",
    body: "Match local successors with your own leaders, expatriates, FutureLab industry mentors or a blend.",
  },
  {
    Icon: ClipboardCheckIcon,
    title: "Knowledge-transfer journey",
    body: "Set goals, sessions, activities and review points around the capabilities each successor needs.",
  },
  {
    Icon: ShieldIcon,
    title: "Programme records",
    body: "Track participation, session completion, feedback and examples from work for internal review.",
  },
] as const;

type Props = {
  resourcesUrl: string;
};

export default function MidaGuidePage({ resourcesUrl }: Props) {
  return (
    <LeadFormProvider>
      <section className="tw-relative tw-border-0 tw-border-b tw-border-solid tw-border-[#d7e4ef] tw-bg-[#f4f8fc] tw-pb-24 tw-pt-20 sm:tw-pb-14 sm:tw-pt-10">
        <div aria-hidden className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-40" style={GRID_BACKDROP} />

        <div className={`${READING} tw-relative`}>
          <Breadcrumb current="MIDA succession plan guide" parents={[{ label: "Resources", href: resourcesUrl }]} />
          <p className="tw-mt-8 tw-mb-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#0068b8]">
            Employer guide · Malaysia
          </p>
          {/* The approved H1, mirrored in static_pages/mida_guide.html.erb; keep both in step. */}
          <h1 className="tw-mt-4 tw-mb-0 tw-text-[clamp(2.75rem,5.1vw,4.5rem)] sm:tw-text-[clamp(2.25rem,8vw,2.75rem)] tw-font-black tw-leading-[1.02] tw-tracking-[-.05em] tw-text-[#10233f] [text-wrap:balance]">
            MIDA succession plan requirement: a practical guide for employers
          </h1>
          <p className="tw-mt-6 tw-mb-0 tw-max-w-[44rem] tw-text-xl tw-leading-8 tw-text-[#536a82] sm:tw-text-lg">
            The key dates, what a plan needs to cover and how to prepare, for HR, talent and business
            leaders managing expatriate roles under Malaysia&rsquo;s Revised Employment Pass Salary
            Policy.
          </p>
          <p className="tw-mt-5 tw-mb-0 tw-text-sm tw-font-bold tw-text-[#5b7188]">
            Last updated <time dateTime="2026-10-01">1 October 2026</time>
          </p>
        </div>
      </section>

      <section className="tw-py-24 sm:tw-py-14">
        <div className={`${READING} tw-grid tw-grid-cols-[1.25fr_.75fr] tw-items-start tw-gap-8 sm:tw-grid-cols-1`}>
          <div className="tw-min-w-0">
            <SectionLabel>Key points</SectionLabel>
            <SectionHeading size="compact">What you need to know</SectionHeading>
            <ul className="tw-mt-8 tw-mb-0 tw-grid tw-list-none tw-gap-3 tw-p-0">
              {KEY_POINTS.map((point) => (
                <li
                  key={point}
                  className="tw-flex tw-gap-3 tw-rounded-2xl tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-5 tw-leading-7 tw-text-[#304d68]"
                >
                  <CircleCheckIcon aria-hidden className="tw-mt-1 tw-h-5 tw-w-5 tw-shrink-0 tw-text-[#0b8c72]" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <aside
            aria-labelledby="mida-key-dates"
            className="tw-rounded-[28px] tw-bg-[#10233f] tw-p-8 tw-text-white xs:tw-p-6"
          >
            <CalendarIcon aria-hidden className="tw-h-7 tw-w-7 tw-text-[#70d9ce]" />
            <h2 id="mida-key-dates" className="tw-mt-4 tw-mb-0 tw-text-2xl tw-font-black tw-tracking-[-.03em]">
              Key dates
            </h2>
            <ol className="tw-mt-6 tw-mb-0 tw-grid tw-list-none tw-gap-6 tw-p-0">
              {KEY_DATES.map(({ date, dateTime, event }) => (
                <li key={dateTime}>
                  <time dateTime={dateTime} className="tw-text-sm tw-font-black tw-text-[#70d9ce]">
                    {date}
                  </time>
                  <p className="tw-mt-1 tw-mb-0 tw-leading-7 tw-text-[#d9e7f1]">{event}</p>
                </li>
              ))}
            </ol>
          </aside>
        </div>
      </section>

      <section className="tw-border-y tw-border-[#d7e4ef] tw-bg-[#f4f8fc] tw-py-24 sm:tw-py-14">
        <div className={READING}>
          <SectionLabel>Who it applies to</SectionLabel>
          <SectionHeading size="compact">Start with your Employment Pass holders</SectionHeading>
          <p className="tw-mt-6 tw-mb-0 tw-max-w-[44rem] tw-text-lg tw-leading-8 tw-text-[#536a82]">
            The ESD summary marks Employment Pass Categories II and III as needing a succession plan. If
            you&rsquo;re submitting new or renewal applications, map the affected roles and dates, then
            confirm the current scope, exemptions, evidence and submission process with MIDA, MYXpats or
            an immigration adviser.
          </p>

          <div className="tw-mt-8 tw-flex tw-gap-4 tw-rounded-[22px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-6 tw-shadow-[0_12px_32px_rgba(32,75,110,.06)] xs:tw-p-5">
            <IconTile icon={AlertIcon} />
            <div>
              <p className="tw-m-0 tw-font-black tw-text-[#173754]">Each role may follow a different process.</p>
              <p className="tw-mt-2 tw-mb-0 tw-leading-7 tw-text-[#536a82]">
                Detailed guidelines and how they&rsquo;re applied may still change. Treat the official
                sources below as the authority.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="tw-py-24 sm:tw-py-14">
        <div className={CONTAINER}>
          <div className="tw-max-w-3xl">
            <SectionLabel>Official definition</SectionLabel>
            <SectionHeading size="compact">Four elements of a structured plan</SectionHeading>
            <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#536a82]">
              These come from the MOHA FAQ. Your final submission should follow the latest official
              format and guidance.
            </p>
          </div>

          <ol className="tw-mt-10 tw-mb-0 tw-grid tw-list-none tw-grid-cols-2 tw-gap-5 tw-p-0 xs:tw-grid-cols-1">
            {PLAN_ELEMENTS.map(({ title, body }, index) => (
              <li
                key={title}
                className="tw-rounded-[24px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-7 tw-shadow-[0_12px_32px_rgba(32,75,110,.05)] xs:tw-p-6"
              >
                <span aria-hidden className="tw-text-sm tw-font-black tw-tabular-nums tw-text-[#0068b8]">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="tw-mt-3 tw-mb-0 tw-text-2xl tw-font-black tw-tracking-[-.03em] tw-text-[#173754]">{title}</h3>
                <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#536a82]">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="tw-bg-[#10233f] tw-py-24 tw-text-white sm:tw-py-14">
        <div className={READING}>
          <SectionLabel tone="mint">Preparation roadmap</SectionLabel>
          <SectionHeading size="compact">Six practical steps to get ready</SectionHeading>

          <ol className="tw-mt-10 tw-mb-0 tw-grid tw-list-none tw-gap-4 tw-p-0">
            {STEPS.map(({ title, body }, index) => (
              <li
                key={title}
                className="tw-flex tw-gap-5 tw-rounded-[22px] tw-border tw-border-white/15 tw-bg-white/[.06] tw-p-6 xs:tw-gap-4 xs:tw-p-5"
              >
                <span
                  aria-hidden
                  className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#087bd3] tw-font-black tw-tabular-nums"
                >
                  {index + 1}
                </span>
                <div>
                  <h3 className="tw-m-0 tw-text-xl tw-font-black tw-text-white">{title}</h3>
                  <p className="tw-mt-2 tw-mb-0 tw-leading-7 tw-text-[#c6d7e7]">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="tw-py-24 sm:tw-py-14">
        <div className={`${READING} tw-grid tw-grid-cols-[.8fr_1.2fr] tw-items-start tw-gap-10 sm:tw-grid-cols-1 sm:tw-gap-8`}>
          <div>
            <FileTextIcon aria-hidden className="tw-h-8 tw-w-8 tw-text-[#087bd3]" />
            <h2 className="tw-mt-4 tw-mb-0 tw-text-[2.25rem] tw-font-black tw-leading-[1.1] tw-tracking-[-.045em] tw-text-[#10233f]">
              Turn the guide into an action list
            </h2>
            <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#536a82]">
              Use the readiness checklist to run an internal working session with HR, business leaders,
              expatriate role holders and possible local successors.
            </p>
          </div>

          <div className="tw-rounded-[28px] tw-border tw-border-[#c5dcea] tw-bg-white tw-p-8 tw-shadow-[0_24px_60px_rgba(23,72,111,.12)] xs:tw-p-6">
            <h3 className="tw-m-0 tw-text-2xl tw-font-black tw-tracking-[-.03em] tw-text-[#173754]">
              Download the guide and readiness checklist
            </h3>
            <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#536a82]">
              Key dates, who&rsquo;s affected, a 12-week action plan and 14 checks to tick off with your
              team, grouped into four areas:
            </p>
            <ul className="tw-mt-5 tw-mb-0 tw-grid tw-list-none tw-grid-cols-2 tw-gap-2 tw-p-0 xs:tw-grid-cols-1">
              {CHECKLIST_AREAS.map((area) => (
                <li
                  key={area}
                  className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-bg-[#f4f8fc] tw-px-4 tw-py-3 tw-text-sm tw-font-bold tw-text-[#304d68]"
                >
                  <span aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-2 tw-border-solid tw-border-[#87b9de]" />
                  {area}
                </li>
              ))}
            </ul>
            <PrimaryButton
              type="button"
              size="lg"
              data-guide-download
              aria-haspopup="dialog"
              className="tw-mt-7 tw-w-full"
            >
              Download the PDF
              <DownloadIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
            </PrimaryButton>
            <p className="tw-mt-3 tw-mb-0 tw-text-center tw-text-sm tw-text-[#5b7188]">
              Free PDF · 7 pages · Not legal advice
            </p>
          </div>
        </div>
      </section>

      <section className="tw-border-0 tw-border-t tw-border-solid tw-border-[#d7e4ef] tw-py-24 sm:tw-py-14">
        <div className={READING}>
          <SectionLabel>Implementation support</SectionLabel>
          <SectionHeading size="compact">Where FutureLab can help</SectionHeading>

          <div className="tw-mt-10 tw-grid tw-grid-cols-3 tw-gap-4 sm:tw-grid-cols-1">
            {SUPPORT.map(({ Icon, title, body }) => (
              <article key={title} className="tw-rounded-[24px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-6">
                <Icon aria-hidden className="tw-h-6 tw-w-6 tw-text-[#087bd3]" />
                <h3 className="tw-mt-4 tw-mb-0 tw-text-xl tw-font-black tw-text-[#173754]">{title}</h3>
                <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#536a82]">{body}</p>
              </article>
            ))}
          </div>

          <p className="tw-mt-6 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#5b7188]">
            We support how you run the plan and keep its records. We don&rsquo;t certify compliance or
            replace advice from MIDA, MYXpats or an immigration adviser.
          </p>
        </div>
      </section>

      <GuideDownload />
    </LeadFormProvider>
  );
}
